import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { removeQueryParams } from "@modules/core/routing/routingHelpers";
import useLocalStorage from "./useLocalStorage";

export const PRESERVED_QUERY_PARAMS_KEY = "preservedQueryParams";

/**
 * Custom hook to preserve query parameters in local storage when navigating between specific paths.
 * @param {string[]} pathsToPreserveQueryParams - Array of paths to preserve query parameters for.
 * @returns {void}
 */
const usePreserveQueryParams = (pathsToPreserveQueryParams: string[]): void => {
  const router = useRouter();
  const didUpdateQueryParams = useRef<boolean>(false);
  const didRestoreQueryParams = useRef<boolean>(false);
  const [preservedQueryParams, setPreservedQueryParams] =
    useLocalStorage<Record<string, string> | null>(
      PRESERVED_QUERY_PARAMS_KEY,
      null
    );

  // Save query params to local storage when the path matches
  useEffect(() => {
    if (
      router.isReady &&
      !didUpdateQueryParams.current &&
      pathsToPreserveQueryParams.includes(router.pathname)
    ) {
      const queryParamsToPreserve: Record<string, string> = {};

      Object.entries(router.query).forEach(([key, value]) => {
        if (typeof value === "string" && value.length > 0) {
          queryParamsToPreserve[key] = value;
        }
        if (value === "") {
          removeQueryParams(router, [key]);
        }
      });

      if (Object.keys(queryParamsToPreserve).length > 0) {
        setPreservedQueryParams(queryParamsToPreserve);
        didUpdateQueryParams.current = true;
      }
    }
  }, [router, pathsToPreserveQueryParams, setPreservedQueryParams]);

  // Restore query params from local storage when the path does not match
  useEffect(() => {
    const currentQueryParams = new URLSearchParams(window.location.search);
    const isPreservedPath = pathsToPreserveQueryParams.includes(
      router.pathname
    );
    if (
      router.isReady &&
      preservedQueryParams &&
      !didRestoreQueryParams.current &&
      (!isPreservedPath ||
        (isPreservedPath && Array.from(currentQueryParams).length === 0))
    ) {
      let didQueryParamsChange = false;

      Object.entries(preservedQueryParams).forEach(([key, value]) => {
        if (!currentQueryParams.has(key)) {
          currentQueryParams.set(key, value);
          didQueryParamsChange = true;
        }
      });

      if (didQueryParamsChange) {
        router.replace(
          `${window.location.pathname}?${currentQueryParams.toString()}`,
          undefined,
          { shallow: true }
        );
        didRestoreQueryParams.current = true;
      }
    }

    const routeChangeStart = () => {
      didUpdateQueryParams.current = false;
      didRestoreQueryParams.current = false;
    };

    router.events.on("routeChangeStart", routeChangeStart);
    return () => {
      router.events.off("routeChangeStart", routeChangeStart);
    };
  }, [router, pathsToPreserveQueryParams, preservedQueryParams]);
};

export default usePreserveQueryParams;
